/**
 * Cart bundle grouper.
 *
 * Theme's <cart-items> custom element (cart-components.js) renders every
 * line-item individually from the `template-cart-item` template and re-
 * syncs itself on every Cart state change. SSR grouping is therefore
 * thrown away on the first update.
 *
 * Instead, subscribe to the cart-store and, after each render, DOM-group
 * siblings sharing a `_bundleHash` property: wrap them in a single
 * `.cart-bundle-wrapper` card, append a combined total row with one
 * remove action that zeros-out every line-item in the bundle.
 */
import Cart from 'cart-store';
import { formatMoney as shopifyFormatMoney } from 'utils';

const WRAPPER_CLASS = 'cart-bundle-wrapper';
const STATE_CLASS = 'cart-item--in-bundle';

// Respect the ACTIVE cart currency so totals in bundle cards match
// Shopify's cart/checkout output in multi-currency (Markets) stores.
function formatMoney(cents) {
  if (cents == null || isNaN(cents)) return '';
  const iso = window.theme?.currency_iso || 'USD';
  try {
    return new Intl.NumberFormat(
      navigator.language || 'en',
      { style: 'currency', currency: iso, currencyDisplay: 'narrowSymbol' }
    ).format(cents / 100);
  } catch {
    const fallback =
      window.theme?.money_total_price_format ||
      window.theme?.money_format ||
      '${{amount}}';
    return shopifyFormatMoney(cents, fallback);
  }
}

function getBundleHash(item) {
  if (!item?.properties) return null;
  // Shopify serializes properties as either a plain object or an array of
  // [key, value] pairs, depending on the source; handle both.
  if (Array.isArray(item.properties)) {
    const entry = item.properties.find(([k]) => k === '_bundleHash');
    return entry ? entry[1] : null;
  }
  return item.properties._bundleHash || null;
}

function buildGroups(items) {
  const groups = new Map();
  items.forEach((item) => {
    const hash = getBundleHash(item);
    if (!hash) return;
    if (!groups.has(hash)) groups.set(hash, []);
    groups.get(hash).push(item);
  });
  return groups;
}

function unwrapAll(root) {
  root.querySelectorAll(`.${WRAPPER_CLASS}`).forEach((wrapper) => {
    const footer = wrapper.querySelector('.cart-bundle-wrapper__footer');
    footer?.remove();
    const frag = document.createDocumentFragment();
    Array.from(wrapper.children).forEach((child) => {
      child.classList.remove(STATE_CLASS);
      child.dataset.bundlePosition = '';
      delete child.dataset.bundleHash;
      frag.appendChild(child);
    });
    wrapper.replaceWith(frag);
  });
}

function renderFooter(hash, items, firstKey) {
  const total = items.reduce((sum, i) => sum + (i.final_line_price || 0), 0);
  const footer = document.createElement('div');
  footer.className = 'cart-bundle-wrapper__footer';
  footer.innerHTML = `
    <div class="cart-bundle-wrapper__total">
      <span class="cart-bundle-wrapper__total-label">Total</span>
      <span class="cart-bundle-wrapper__total-value">${formatMoney(total)}</span>
    </div>
    <button
      type="button"
      class="cart-bundle-wrapper__remove"
      data-bundle-hash="${hash}"
      data-first-key="${firstKey}"
      aria-label="Remove bundle"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Remove</span>
    </button>
  `;

  footer.querySelector('[data-bundle-hash]').addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    btn.disabled = true;
    // Remove every line-item in the bundle in ONE request. Cart.update (the
    // instance method — not the static Cart.updateCart) POSTs a single
    // /cart/update.js with all keys → 0 AND writes the result back to the
    // zustand store, so subscribers re-render once. Cart.change is per-line
    // and would fire N sequential writes (the items disappearing one by one).
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });
    try {
      await Cart.update({ updates });
    } catch (err) {
      console.error('[cart-bundle] remove failed', err);
      btn.disabled = false;
    }
  });

  return footer;
}

function groupContainer(container, state) {
  const items = state?.cart?.items || [];
  const groups = buildGroups(items);

  // Start fresh to keep DOM in sync when state shrinks/mutates.
  unwrapAll(container);
  if (groups.size === 0) return;

  groups.forEach((bundleItems, hash) => {
    if (bundleItems.length < 2) return;

    const els = bundleItems
      .map((i) => container.querySelector(`cart-item[key="${i.key}"]`))
      .filter(Boolean);

    if (els.length < 2) return;

    const wrapper = document.createElement('div');
    wrapper.className = WRAPPER_CLASS;
    wrapper.dataset.bundleHash = hash;

    els[0].before(wrapper);
    els.forEach((el, i) => {
      el.classList.add(STATE_CLASS);
      el.dataset.bundleHash = hash;
      el.dataset.bundlePosition = i === 0 ? 'first' : i === els.length - 1 ? 'last' : 'middle';
      wrapper.appendChild(el);
    });

    wrapper.appendChild(renderFooter(hash, bundleItems, bundleItems[0].key));
  });
}

function regroupAll(state) {
  document.querySelectorAll('cart-items').forEach((c) => groupContainer(c, state));
}

// Subscribe once per page.
if (!window.__cartBundleInit) {
  window.__cartBundleInit = true;

  const init = () => {
    // First pass with current state (handles initial SSR page).
    regroupAll(Cart.getState());
    Cart.subscribe((state) => {
      // cart-components.js mutates DOM inside its own subscribe callback.
      // Run after that via rAF so we see the settled layout.
      requestAnimationFrame(() => regroupAll(state));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
